/**
 * EditDeviceLifecycleSections
 *
 * Renders the device's lifecycle sub-records (procurement/shipping, device keys,
 * provisioning, fault, retirement) as individually editable sections on the Edit
 * Device page. Each section persists through its own dedicated upsert endpoint
 * using a fetch-merge-update: the existing sub-record is merged with the edited
 * fields so untouched values (and the record's business key + status) are
 * preserved.
 *
 * These edits are data corrections, NOT lifecycle transitions — the dedicated
 * *Info endpoints never touch `device.deviceStatus`, and each payload re-sends
 * the existing `*Status` value unchanged rather than a new one.
 *
 * Sections render only when their sub-record exists, mirroring the detail page.
 */

import { Truck, KeyRound, Power, AlertTriangle, Archive } from 'lucide-react'
import { useApiQuery, useApiMutation } from '@/api/ApiQueryServiceProvider'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'
import { useDeviceProfileNetwork } from '@/hooks/useDeviceProfileNetwork'
import { LifecycleSectionForm, type LifecycleSectionField } from './LifecycleSectionForm'

const log = AppLogger.getLogger('components/sensory/devices/EditDeviceLifecycleSections')

// ---- value converters ------------------------------------------------------

const epochToIso = (epoch?: number | null): string =>
  epoch ? new Date(epoch * 1000).toISOString() : ''

const isoToEpoch = (value: unknown): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const t = new Date(value).getTime()
  return Number.isFinite(t) ? Math.floor(t / 1000) : undefined
}

const toNumber = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

const toStr = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined
  const s = String(value).trim()
  return s === '' ? undefined : s
}

// ---- props -----------------------------------------------------------------

interface DeviceLifecycleData {
  device?: Record<string, any>
  procurementInfo?: Record<string, any> | null
  provisioningInfo?: Record<string, any> | null
  faultInfo?: Record<string, any> | null
  retirementInfo?: Record<string, any> | null
}

export interface EditDeviceLifecycleSectionsProps {
  deviceNumber: number
  deviceData: DeviceLifecycleData
}

const SHIPPER_OPTIONS = [
  { value: 'ups', label: 'UPS' },
  { value: 'usps', label: 'USPS' },
  { value: 'fedex', label: 'FedEx' },
]

export function EditDeviceLifecycleSections({ deviceNumber, deviceData }: EditDeviceLifecycleSectionsProps) {
  const { toast } = useToast()

  const device = deviceData.device ?? {}
  const procurementInfo = deviceData.procurementInfo ?? undefined
  const provisioningInfo = deviceData.provisioningInfo ?? undefined
  const faultInfo = deviceData.faultInfo ?? undefined
  const retirementInfo = deviceData.retirementInfo ?? undefined

  // Device keys live in a separate record linked by device.deviceKeyInfoNumber.
  const deviceKeyInfoNumber: number | undefined = device.deviceKeyInfoNumber ?? undefined
  const { data: keyInfoResponse } = useApiQuery<{
    content?: { deviceKeyInfo?: Record<string, any> }
  }>(
    'getDeviceKeyInfo',
    { deviceKeyInfoNumber: deviceKeyInfoNumber ?? 0 },
    { enabled: !!deviceKeyInfoNumber }
  )
  const keyInfo = keyInfoResponse?.content?.deviceKeyInfo

  // The Device Keys credential set depends on the device's network type (Feature 4).
  const { credentialGroup } = useDeviceProfileNetwork(deviceData)

  // Select options for the distributor / corridor / site fields.
  const { optionsMap, loadingMap } = useSelectOptionsMap(
    {
      distributorNumber: {
        endpoint: 'getDeviceDistributors',
        dataKey: 'distributors',
        valueField: 'distributorNumber',
        labelField: 'distributorName',
      },
      networkCorridorNumber: {
        endpoint: 'getNetworkCorridors',
        dataKey: 'networkCorridors',
        valueField: 'networkCorridorNumber',
        labelField: 'networkCorridorName',
      },
      networkSiteNumber: {
        endpoint: 'getNetworkSites',
        dataKey: 'networkSites',
        valueField: 'networkSiteNumber',
        labelField: 'networkSiteName',
      },
    },
    {}
  )

  // Dedicated per-section upsert mutations. None of these touch device.deviceStatus.
  const invalidates = ['getDeviceByNumber', 'getDevices']
  const onError = (label: string) => (error: Error) => {
    log.error(`Failed to save ${label}:`, error)
    toast({ title: 'Error', description: `Failed to save ${label}: ${error.message}`, type: 'error' })
  }
  const onOk = (label: string) => () =>
    toast({ title: 'Saved', description: `${label} updated.`, type: 'success' })

  const saveProcurement = useApiMutation('upsertDeviceProcurementInfo', 'POST', {
    invalidates, onSuccess: onOk('Procurement & shipping'), onError: onError('procurement & shipping'),
  })
  const saveKeyInfo = useApiMutation('upsertDeviceKeyInfo', 'POST', {
    invalidates, onSuccess: onOk('Device keys'), onError: onError('device keys'),
  })
  const saveProvisioning = useApiMutation('upsertDeviceProvisioningInfo', 'POST', {
    invalidates, onSuccess: onOk('Provisioning'), onError: onError('provisioning'),
  })
  const saveFault = useApiMutation('upsertDeviceFaultInfo', 'POST', {
    invalidates, onSuccess: onOk('Fault'), onError: onError('fault'),
  })
  const saveRetirement = useApiMutation('upsertDeviceRetirementInfo', 'POST', {
    invalidates, onSuccess: onOk('Retirement'), onError: onError('retirement'),
  })

  // Device Keys section — fields + prefill + payload keyed to the network group (Feature 4).
  const keySection: {
    fields: LifecycleSectionField[]
    initialValues: Record<string, unknown>
    build: (v: Record<string, unknown>) => Record<string, unknown>
  } =
    credentialGroup === 'cellular'
      ? {
          fields: [
            { name: 'deviceSerialNumber', label: 'Serial Number', control: 'text' },
            { name: 'simCardNumber', label: 'SIM Card Number', control: 'text' },
          ],
          initialValues: {
            deviceSerialNumber: keyInfo?.deviceSerialNumber ?? '',
            simCardNumber: keyInfo?.simCardNumber ?? '',
          },
          build: (v) => ({ deviceSerialNumber: toStr(v.deviceSerialNumber), simCardNumber: toStr(v.simCardNumber) }),
        }
      : credentialGroup === 'wifi'
        ? {
            fields: [
              { name: 'deviceSerialNumber', label: 'Serial Number', control: 'text' },
              { name: 'ssid', label: 'SSID', control: 'text' },
              { name: 'wifiPassword', label: 'Password', control: 'text' },
            ],
            initialValues: {
              deviceSerialNumber: keyInfo?.deviceSerialNumber ?? '',
              ssid: keyInfo?.ssid ?? '',
              wifiPassword: keyInfo?.wifiPassword ?? '',
            },
            build: (v) => ({
              deviceSerialNumber: toStr(v.deviceSerialNumber),
              ssid: toStr(v.ssid),
              wifiPassword: toStr(v.wifiPassword),
            }),
          }
        : {
            fields: [
              { name: 'deviceSerialNumber', label: 'Serial Number', control: 'text' },
              { name: 'deviceEUI', label: 'Device EUI', control: 'text' },
              { name: 'deviceJoinEUI', label: 'Join EUI', control: 'text' },
              { name: 'deviceAppKeyRootKey', label: 'App Key', control: 'text' },
              { name: 'deviceAddress', label: 'Device Address', control: 'text' },
              { name: 'deviceNETSKey', label: 'NETS Key', control: 'text' },
              { name: 'deviceAPPSKey', label: 'APPS Key', control: 'text' },
            ],
            initialValues: {
              deviceSerialNumber: keyInfo?.deviceSerialNumber ?? '',
              deviceEUI: keyInfo?.deviceEUI ?? '',
              deviceJoinEUI: keyInfo?.deviceJoinEUI ?? '',
              deviceAppKeyRootKey: keyInfo?.deviceAppKey?.rootKey ?? '',
              deviceAddress: keyInfo?.deviceAddress ?? '',
              deviceNETSKey: keyInfo?.deviceNETSKey ?? '',
              deviceAPPSKey: keyInfo?.deviceAPPSKey ?? '',
            },
            build: (v) => ({
              deviceEUI: toStr(v.deviceEUI),
              deviceJoinEUI: toStr(v.deviceJoinEUI),
              deviceAppKey: { rootKey: toStr(v.deviceAppKeyRootKey) },
              deviceAddress: toStr(v.deviceAddress),
              deviceNETSKey: toStr(v.deviceNETSKey),
              deviceAPPSKey: toStr(v.deviceAPPSKey),
              deviceSerialNumber: toStr(v.deviceSerialNumber),
            }),
          }

  return (
    <div className="space-y-4">
      {/* Procurement & Shipping (procurementInfo) */}
      {procurementInfo && (
        <LifecycleSectionForm
          title="Procurement & Shipping"
          description="Correct the ordering and shipping details. Does not change the device status."
          icon={Truck}
          isSaving={saveProcurement.isPending}
          fields={[
            { name: 'deviceOrderDate', label: 'Order Date', control: 'date' },
            { name: 'devicePurchaseOrderNumber', label: 'Purchase Order Number', control: 'number' },
            { name: 'deviceDistributorNumber', label: 'Distributor', control: 'select', options: optionsMap.distributorNumber ?? [], isLoading: loadingMap.distributorNumber },
            { name: 'shipperName', label: 'Carrier', control: 'select', options: SHIPPER_OPTIONS },
            { name: 'deviceShippingDate', label: 'Shipping Date', control: 'date' },
            { name: 'deviceShippingTrackingNumber', label: 'Tracking Number', control: 'text' },
            { name: 'deviceDeliveryDate', label: 'Delivery Date', control: 'date' },
          ]}
          initialValues={{
            deviceOrderDate: epochToIso(procurementInfo.deviceOrderDate),
            devicePurchaseOrderNumber: procurementInfo.devicePurchaseOrderNumber ?? '',
            deviceDistributorNumber: procurementInfo.deviceDistributorNumber != null ? String(procurementInfo.deviceDistributorNumber) : '',
            shipperName: procurementInfo.shipperName ?? '',
            deviceShippingDate: epochToIso(procurementInfo.deviceShippingDate),
            deviceShippingTrackingNumber: procurementInfo.deviceShippingTrackingNumber ?? '',
            deviceDeliveryDate: epochToIso(procurementInfo.deviceDeliveryDate),
          }}
          onSave={async (v) => {
            const merged = {
              // preserved (not edited here)
              deviceProcurementInfoNumber: procurementInfo.deviceProcurementInfoNumber ?? undefined,
              deviceNumber,
              deviceProcurementStatus: procurementInfo.deviceProcurementStatus ?? undefined,
              deviceTypeNumber: procurementInfo.deviceTypeNumber ?? undefined,
              deviceShippedToAddressNumber:
                procurementInfo.deviceShippedToAddress?.addressNumber ??
                procurementInfo.deviceShippedToAddressNumber ??
                undefined,
              // edited
              deviceOrderDate: isoToEpoch(v.deviceOrderDate),
              devicePurchaseOrderNumber: toNumber(v.devicePurchaseOrderNumber),
              deviceDistributorNumber: toNumber(v.deviceDistributorNumber),
              shipperName: toStr(v.shipperName),
              deviceShippingDate: isoToEpoch(v.deviceShippingDate),
              deviceShippingTrackingNumber: toStr(v.deviceShippingTrackingNumber),
              deviceDeliveryDate: isoToEpoch(v.deviceDeliveryDate),
            }
            await saveProcurement.mutateAsync({ deviceNumber, procurementInfo: merged })
          }}
        />
      )}

      {/* Device Keys (deviceKeyInfo) — credential set depends on network type (Feature 4) */}
      {deviceKeyInfoNumber && keyInfo && (
        <LifecycleSectionForm
          title="Device Keys"
          description="Correct the device identifiers and credentials."
          icon={KeyRound}
          isSaving={saveKeyInfo.isPending}
          fields={keySection.fields}
          initialValues={keySection.initialValues}
          onSave={async (v) => {
            const merged = {
              deviceKeyInfoNumber: keyInfo.deviceKeyInfoNumber ?? deviceKeyInfoNumber,
              deviceNumber,
              archiveDate: keyInfo.archiveDate ?? 0,
              ...keySection.build(v),
            }
            await saveKeyInfo.mutateAsync(merged)
          }}
        />
      )}

      {/* Provisioning (provisioningInfo) */}
      {provisioningInfo && (
        <LifecycleSectionForm
          title="Provisioning"
          description="Correct the network assignment. Does not change the provisioning status."
          icon={Power}
          isSaving={saveProvisioning.isPending}
          fields={[
            { name: 'networkCorridorNumber', label: 'Network Corridor', control: 'select', options: optionsMap.networkCorridorNumber ?? [], isLoading: loadingMap.networkCorridorNumber },
            { name: 'networkSiteNumber', label: 'Network Site', control: 'select', options: optionsMap.networkSiteNumber ?? [], isLoading: loadingMap.networkSiteNumber },
          ]}
          initialValues={{
            networkCorridorNumber: provisioningInfo.networkCorridorNumber != null ? String(provisioningInfo.networkCorridorNumber) : '',
            networkSiteNumber: provisioningInfo.networkSiteNumber != null ? String(provisioningInfo.networkSiteNumber) : '',
          }}
          onSave={async (v) => {
            const merged = {
              deviceProvisioningInfoNumber: provisioningInfo.deviceProvisioningInfoNumber ?? undefined,
              deviceNumber,
              // preserved status/type/dates (unchanged)
              deviceProvisioningStatus: provisioningInfo.deviceProvisioningStatus ?? undefined,
              deviceProvisioningType: provisioningInfo.deviceProvisioningType ?? undefined,
              deviceProvisionRequestedDate: provisioningInfo.deviceProvisionRequestedDate ?? undefined,
              deviceProvisionCompletedDate: provisioningInfo.deviceProvisionCompletedDate ?? undefined,
              deviceDeprovisionDate: provisioningInfo.deviceDeprovisionDate ?? undefined,
              // edited
              networkCorridorNumber: toNumber(v.networkCorridorNumber),
              networkSiteNumber: toNumber(v.networkSiteNumber),
            }
            await saveProvisioning.mutateAsync({ deviceNumber, provisioningInfo: merged })
          }}
        />
      )}

      {/* Fault (faultInfo) */}
      {faultInfo && (
        <LifecycleSectionForm
          title="Fault"
          description="Correct the fault record."
          icon={AlertTriangle}
          isSaving={saveFault.isPending}
          fields={[
            { name: 'deviceFaultyDate', label: 'Fault Date', control: 'date' },
            { name: 'deviceFaultyNotes', label: 'Fault Notes', control: 'textarea' },
            { name: 'deviceReturned', label: 'Device Returned', control: 'checkbox' },
          ]}
          initialValues={{
            deviceFaultyDate: epochToIso(faultInfo.deviceFaultyDate),
            deviceFaultyNotes: faultInfo.deviceFaultyNotes ?? '',
            deviceReturned: faultInfo.deviceReturned ?? false,
          }}
          onSave={async (v) => {
            const merged = {
              deviceFaultInfoNumber: faultInfo.deviceFaultInfoNumber ?? undefined,
              deviceNumber,
              deviceFaultyDate: isoToEpoch(v.deviceFaultyDate),
              deviceFaultyNotes: toStr(v.deviceFaultyNotes) ?? '',
              deviceReturned: Boolean(v.deviceReturned),
            }
            await saveFault.mutateAsync({ deviceNumber, faultInfo: merged })
          }}
        />
      )}

      {/* Retirement (retirementInfo) */}
      {retirementInfo && (
        <LifecycleSectionForm
          title="Retirement"
          description="Correct the retirement date."
          icon={Archive}
          isSaving={saveRetirement.isPending}
          fields={[{ name: 'deviceRetirementDate', label: 'Retirement Date', control: 'date' }]}
          initialValues={{ deviceRetirementDate: epochToIso(retirementInfo.deviceRetirementDate) }}
          onSave={async (v) => {
            const merged = {
              deviceRetirementInfoNumber: retirementInfo.deviceRetirementInfoNumber ?? undefined,
              deviceNumber,
              deviceRetirementDate: isoToEpoch(v.deviceRetirementDate),
            }
            await saveRetirement.mutateAsync({ deviceNumber, retirementInfo: merged })
          }}
        />
      )}
    </div>
  )
}
